/**
 * 260923 미디어 transport 가 깨졌을 때 ICE 만 다시 붙여 보는 복구 정책.
 *
 * 왜 있나: 지금은 connectionstatechange 가 disconnected 가 되면 곧바로 consumer 를 닫고
 *          exitConsume 까지 보낸다. 서버는 그때 receiver 를 지우므로 그 뒤에 ICE 재시작을
 *          요청해도 늘 RECEIVER_INFO_NOT_FOUND 다. 그래서 disconnected 를 「끊김」이 아니라
 *          「판단 보류」로 바꾸고, failed 에 restartIce 를 한 번 시도한 뒤
 *          실패·무응답이면 예전 정리 흐름(fallback)으로 그대로 떨어진다.
 *
 * 이 모듈은 판단만 한다 — 전역도 타이머도 import 도 없다. 부작용은 전부 주입된 콜백이다.
 * (미디어 서버 회신 `프론트엔드_restartIce_회신_20260923.md` §4-1·4-2,
 *  공동작업 계획 `네트워크_자동재연결_공동작업_계획_20260916.md` 규칙 ①②)
 */

/**
 * 보류 한도. 이만큼 disconnected 가 이어지면 요청한다.
 * 260923 처음엔 「failed 가 끝내 안 오는 경우의 안전망」으로 30초를 뒀는데, Pro 실기 61차에서
 * 수신 transport 에는 애초에 failed 가 오지 않는 것이 드러났다(네 번 모두). 즉 이것이
 * 안전망이 아니라 실제 방아쇠다. 길게 두면 그만큼 예전 정리가 늦어진다 —
 * AP 로밍·순간 흔들림은 1~3초라 10초면 관용은 그대로 두고 늦는 폭만 줄인다.
 */
export const HOLD_LIMIT_MS = 10000;

/**
 * 260923 restartIce 를 이미 한 번 써 본 transport 의 보류 한도.
 * 그 뒤로는 기다려도 restartIce 를 또 쓰지 않으므로 오래 붙잡고 있을 이유가 없다.
 * 그렇다고 0 으로 두면 첫 restartIce 이후 수업 내내 짧은 흔들림마다 수업이 한 번씩 끊긴다.
 */
export const USED_HOLD_LIMIT_MS = 5000;

/** 서버 응답을 기다리는 시간. 이 액션을 모르는 서버 판은 오류가 아니라 무응답이다. */
export const RESPONSE_TIMEOUT_MS = 3000;

/**
 * 260928 restartIce 를 건 뒤 실제로 붙는지 지켜보는 시간.
 *
 * ⚠ 이것이 없으면 영구 정지한다. restartIce 를 적용한 시점의 transport 는 이미 disconnected 이고
 * onState 는 상태가 "바뀔 때만" 불린다. 그래서 출구가 connected 와 failed 둘뿐인데,
 * 붙어 있다가 끊긴 transport 에는 브라우저가 failed 를 안 내는 것으로 보인다 —
 * Pro 실기 67차 D 에서 크롬이 5분 5초 멈췄다(차단 해제 뒤 3분 51초 포함, 사람이 나가야 끝났다).
 * 같은 차단에서 used 라 restartIce 를 못 쓴 쪽은 fallback→재생성 사이클이 새 이벤트를 만들어
 * 해제 11초 뒤 스스로 복구했다 — 쓸 수 있었던 쪽이 죽고 못 쓰는 쪽이 살았다.
 *
 * 값의 근거: 성공은 늘 1초 안쪽이었다(66차 612ms · 67차 24ms · 서버 36·42ms).
 * 3초면 성공을 자르지 않고, 미디어 서버의 30초 감시와도 겹치지 않는다.
 * (Pro 실기 67차 §1·§3 · 미디어 서버 67차 회신 §2-1·§3)
 */
export const RESTART_SETTLE_MS = 3000;

/**
 * 260930 fallback 에 넘기는 포기 이유. Android 송출은 이것을 callRestartProducerTransportFailed(reason) 로
 * 네이티브에 그대로 보낸다 — 네이티브 처리는 이유와 무관하게 같고(「지금 정리」) 로그용이다.
 * ⚠ 이름은 Android 와 합의한 것이다. 바꾸거나 늘리려면 먼저 알린다.
 * (프론트 web-frontend-native-producer-restart-recheck-20260930.md §2-2)
 * 합의 목록의 "stale" 은 조정자가 아니라 부르는 쪽이 판정하므로 여기 없다.
 */
export const REASON = {
    FAILED_RESPONSE : "failed-response",   // 서버가 거절했거나, 다른 transport 의 응답이거나, 받은 응답을 적용하지 못했다
    TIMEOUT         : "timeout",           // 요청 뒤 응답이 안 왔다
    SETTLE_TIMEOUT  : "settle-timeout",    // restartIce 를 건 뒤 정착 시간 안에 안 붙었다
    USED            : "used"               // 이미 restartIce 를 한 번 쓴 transport 가 또 끊겼다
};

/**
 * transport 하나에 하나씩 만든다. transport 를 새로 만들면 조정자도 새로 만든다.
 *
 * @param {Object}   handlers
 * @param {Function} handlers.sendRequest - 서버에 restart 요청을 보낸다
 * @param {Function} handlers.restartIce  - transport.restartIce({ iceParameters }) 를 부른다. Promise 반환
 * @param {Function} handlers.fallback    - 예전 정리 흐름을 그대로 실행한다. 인자로 포기 이유(REASON 중 하나)를 받는다
 * @param {Function} handlers.setWake     - ms 뒤에 onWake() 를 불러 달라
 * @param {Function} handlers.clearWake   - 걸린 타이머를 해제한다
 */
export const createIceRestartCoordinator = ({ sendRequest, restartIce, fallback, setWake, clearWake }) => {
    let phase = "idle";     // idle | holding | requested | restarted
    // 이 transport 에 restartIce 를 이미 써 봤다.
    // 260923 ⚠ 이것을 풀어 재시도를 늘리려면 조건이 하나 붙는다 — 서버는 restartIce() 를 두 번째로
    // 부르는 순간 처음 보관해 둔 원래 자격증명을 지우므로, 옛 자격증명으로 붙어 있던 연결이 그때
    // 실제로 끊긴다. 재요청은 반드시 「직전 응답을 적용한 뒤」에만 해야 한다.
    // (미디어 서버 회신 프론트엔드_ICE재시작연동_회신_20260923.md §2-②)
    let used = false;

    const request = () => {
        phase = "requested";
        used = true;
        // 260923 타이머를 먼저 건다. sendRequest 가 던지면 타이머 없이 requested 에 갇혀
        // fallback 이 영영 돌지 않는다.
        setWake(RESPONSE_TIMEOUT_MS);
        sendRequest();
    };

    const giveUp = (reason) => {
        phase = "idle";
        clearWake();
        fallback(reason);
    };

    return {
        getPhase: () => phase,

        reset: () => {
            phase = "idle";
            used = false;
            clearWake();
        },

        onState: (state) => {
            if (state === "disconnected") {
                // 아직 끊겼다고 보지 않는다. 짧은 흔들림·AP 로밍은 여기서 조용히 끝난다.
                // 260923 이미 한 번 써 본 transport 는 짧게만 본다 — 기다려도 restartIce 를
                // 또 쓰지 않으므로 30초를 채우는 것은 화면이 늦게 돌아오는 것일 뿐이다.
                if (phase === "idle") {
                    phase = "holding";
                    setWake(used ? USED_HOLD_LIMIT_MS : HOLD_LIMIT_MS);
                }
                return;
            }

            if (state === "failed") {
                if (phase === "requested") return;  // 이미 물어보는 중이다
                if (used) {
                    giveUp(REASON.USED);
                } else {
                    clearWake();
                    request();
                }
                return;
            }

            if (state === "connected") {
                // 260923 보류 중이든 응답을 기다리는 중이든, 붙었으면 끝이다.
                // requested 에서 이것을 안 받으면 자가 복구한 transport 를 응답 타임아웃이 정리해 버린다.
                // 늦게 오는 응답은 onResponse 의 phase 검사에서 버려지고, used 는 true 로 남아
                // restartIce 를 두 번 쓰지 않는다는 규칙도 그대로다.
                // 260928 restarted 도 여기서 끝난다 — 정착 타이머를 끄는 자리다.
                if (phase === "holding" || phase === "requested" || phase === "restarted") {
                    phase = "idle";
                    clearWake();
                }
                return;
            }

            if (state === "closed") {
                phase = "idle";
                clearWake();
            }
        },

        onWake: () => {
            if (phase === "holding") {
                // failed 가 끝내 오지 않았다. 안전망.
                if (used) {
                    giveUp(REASON.USED);
                } else {
                    clearWake();
                    request();
                }
                return;
            }

            if (phase === "requested") {
                giveUp(REASON.TIMEOUT);   // 응답 타임아웃
                return;
            }

            if (phase === "restarted") {
                // 260928 restartIce 를 걸었는데 정착 시간 안에 안 붙었다.
                // 브라우저의 failed 를 기다리면 영영 안 올 수 있다(RESTART_SETTLE_MS 주석).
                giveUp(REASON.SETTLE_TIMEOUT);
            }
        },

        onResponse: (rtnVal, currentTransportId) => {
            if (phase !== "requested") return Promise.resolve();   // 늦은 응답

            clearWake();

            const isUsable = rtnVal && rtnVal.isSuccess === true &&
                (rtnVal.transportId === undefined || rtnVal.transportId === currentTransportId);

            if (!isUsable) {
                phase = "idle";
                fallback(REASON.FAILED_RESPONSE);
                return Promise.resolve();
            }

            // 260928 붙을 때까지 손을 놓지 않는다. 타이머를 먼저 걸고 적용한다 —
            // restartIce 가 던지거나 오래 걸려도 깨울 방아쇠가 남아 있어야 한다.
            phase = "restarted";
            setWake(RESTART_SETTLE_MS);

            return Promise.resolve()
                .then(() => restartIce(rtnVal.iceParameters))
                .catch((err) => {
                    console.log("iceRestartCoordinator - restartIce error => ", err);
                    // 정착 타이머가 이미 만료돼 giveUp() 이 돌았으면 phase 가 idle 이다. 두 번 하지 않는다.
                    if (phase === "restarted") {
                        giveUp(REASON.FAILED_RESPONSE);   // 받은 응답을 적용하지 못했다
                    }
                });
        }
    };
}
